/**
 * The template vocabulary: what each block type is called, what each status
 * means, and what a validation failure from the API is *about* in plain words.
 *
 * It exists for the same reason `segment-display.ts` does — so the list, the
 * editor and the preview cannot describe the same thing three different ways —
 * and it obeys the same rule: **it invents no claim about a template.** Whether
 * a document compiles, which variables it uses, and what still stops it being
 * approved are all answers the API gives; this file decides only the wording
 * around them.
 *
 * Two things are deliberately imported rather than re-stated. The variables a
 * contact can supply come from `CONTACT_VARIABLE_NAMES`, which `@wend/shared`
 * derives from the contact contract itself — a list re-typed here would go stale
 * the day a field is renamed and would offer people a variable that no longer
 * resolves. And the messages for approval blockers and unresolvable variables
 * come from the API verbatim; what this file adds is the heading above them, not
 * a second account of the same problem that could disagree with the first.
 */
import {
  CONTACT_VARIABLE_NAMES,
  EXTRAS_VARIABLE_PREFIX,
  templateVariableToken,
  type BlockAlign,
  type FieldError,
  type HeadingLevel,
  type TemplateApprovalBlocker,
  type TemplateBlock,
  type TemplateBlockType,
  TEMPLATE_BACKGROUND_VALUES,
  TEMPLATE_BODY_WIDTH_VALUES,
  type TemplateBackground,
  type TemplateBodyWidth,
  type TemplateStatus,
} from '@wend/shared';

/* -------------------------------------------------------------------------- */
/* Blocks                                                                      */
/* -------------------------------------------------------------------------- */

export interface BlockDisplay {
  /** What the block is called, lower case: it appears mid-sentence in labels. */
  readonly label: string;
  /** One line in the "add a block" menu, saying what it is for. */
  readonly purpose: string;
}

/**
 * One entry per member of `TemplateBlockType`.
 *
 * The wording avoids design words — "hero", "CTA", "module" — because the person
 * building the email is a marketer at a retailer, not someone who has read an
 * email framework's documentation. A button is a button.
 */
export const TEMPLATE_BLOCK_DISPLAY: Readonly<Record<TemplateBlockType, BlockDisplay>> = {
  heading: {
    label: 'heading',
    purpose: 'A line of larger text that starts a section',
  },
  text: {
    label: 'paragraph',
    purpose: 'Your message. Line breaks are kept',
  },
  image: {
    label: 'image',
    purpose: 'A picture you have uploaded to this template',
  },
  button: {
    label: 'button',
    purpose: 'A link to press, e.g. the offer it is about',
  },
  divider: {
    label: 'divider',
    purpose: 'A horizontal rule between two sections',
  },
  spacer: {
    label: 'spacer',
    purpose: 'Empty vertical space',
  },
  footer: {
    label: 'unsubscribe footer',
    purpose: 'Required in every template. It cannot be added or removed',
  },
};

/** The block types a person may add. The footer is not one of them — see below. */
export const ADDABLE_BLOCK_TYPES = [
  'heading',
  'text',
  'button',
  'image',
  'divider',
  'spacer',
] as const satisfies readonly TemplateBlockType[];

export type AddableBlockType = (typeof ADDABLE_BLOCK_TYPES)[number];

/**
 * Why the footer has no delete control and cannot be moved off the end.
 *
 * Said on the screen, next to the block, rather than left as a disabled button
 * with no explanation — a control that does nothing and says nothing reads as a
 * bug, and someone will file it as one. It is also the one rule in this editor
 * with a consequence outside Wend, which is worth a sentence of its own.
 */
export const FOOTER_LOCKED_REASON =
  'Every template ends with this footer and it cannot be removed. A marketing email without a working unsubscribe is how a sending account gets suspended — and under BYO that is your own AWS account, not Wend’s.';

/** Why the last block above the footer cannot move down. */
export const FOOTER_STAYS_LAST =
  'The unsubscribe footer stays last, so nothing can move below it.';

/**
 * A short quotation of what a block actually says, for a control's label.
 *
 * "Move heading up" is fine in a template with one heading and useless in a
 * template with four. This is what turns the label into "Move heading “Winter
 * sale” up", which is the difference between a screen reader user being able to
 * reorder the document and having to count arrows.
 *
 * Empty for the blocks that have no text of their own; the caller falls back to
 * the type name alone.
 */
export function describeBlock(block: TemplateBlock, maxLength = 40): string {
  const raw =
    block.type === 'heading' || block.type === 'text' || block.type === 'button'
      ? block.text
      : block.type === 'image'
        ? block.alt
        : '';

  const flattened = raw.replace(/\s+/g, ' ').trim();
  if (flattened === '') return '';
  return flattened.length <= maxLength ? flattened : `${flattened.slice(0, maxLength - 1)}…`;
}

/** `heading “Winter sale”`, or just `divider`. The subject of every block label. */
export function blockSubject(block: TemplateBlock): string {
  const label = TEMPLATE_BLOCK_DISPLAY[block.type].label;
  const snippet = describeBlock(block);
  return snippet === '' ? label : `${label} “${snippet}”`;
}

/**
 * The accessible name of a move control.
 *
 * Reordering here is buttons and the keyboard rather than drag-and-drop, which
 * means these labels *are* the reordering interface for anyone not using a
 * mouse. Two anonymous arrows would technically be operable and practically
 * unusable, so each one names what it moves and where it is going.
 */
export function moveBlockLabel(
  block: TemplateBlock,
  direction: 'up' | 'down',
  position: { index: number; total: number },
): string {
  return `Move ${blockSubject(block)} ${direction} — currently block ${String(position.index + 1)} of ${String(position.total)}`;
}

export function deleteBlockLabel(block: TemplateBlock): string {
  return `Delete ${blockSubject(block)}`;
}

/* -------------------------------------------------------------------------- */
/* Block field vocabulary                                                      */
/* -------------------------------------------------------------------------- */

export const BLOCK_ALIGN_LABEL: Readonly<Record<BlockAlign, string>> = {
  left: 'Left',
  center: 'Centre',
  right: 'Right',
};

export const HEADING_LEVEL_LABEL: Readonly<Record<HeadingLevel, string>> = {
  1: 'Level 1 — the largest',
  2: 'Level 2',
  3: 'Level 3 — the smallest',
};

/* -------------------------------------------------------------------------- */
/* Template settings — slice 56                                                */
/* -------------------------------------------------------------------------- */

/**
 * The two template-level settings' labels, one per value the contract allows.
 *
 * `Record`s over the contract's own unions, so a value added there is a compile
 * error here until it has a label. The options a `Select` offers are the
 * contract's value lists in the contract's order — the default first, because
 * it is the one a person is choosing *against*.
 *
 * Labels only, per the standing UI rule. What each value costs in Outlook
 * desktop and in dark mode is argued in the contract and in the API's renderer,
 * where the markup that causes it is; a sentence here would be a second account
 * of it on the screen where somebody is picking a background, not reading about
 * one.
 */
export const TEMPLATE_BODY_WIDTH_LABEL: Readonly<Record<TemplateBodyWidth, string>> = {
  standard: 'Standard',
  full: 'Full width',
};

export const TEMPLATE_BACKGROUND_LABEL: Readonly<Record<TemplateBackground, string>> = {
  white: 'White',
  transparent: 'Transparent',
};

export const TEMPLATE_BODY_WIDTH_OPTIONS = TEMPLATE_BODY_WIDTH_VALUES.map((value) => ({
  value,
  label: TEMPLATE_BODY_WIDTH_LABEL[value],
}));

export const TEMPLATE_BACKGROUND_OPTIONS = TEMPLATE_BACKGROUND_VALUES.map((value) => ({
  value,
  label: TEMPLATE_BACKGROUND_LABEL[value],
}));

export function isTemplateBodyWidth(value: string): value is TemplateBodyWidth {
  return TEMPLATE_BODY_WIDTH_VALUES.some((known) => known === value);
}

export function isTemplateBackground(value: string): value is TemplateBackground {
  return TEMPLATE_BACKGROUND_VALUES.some((known) => known === value);
}

/* -------------------------------------------------------------------------- */
/* Status                                                                      */
/* -------------------------------------------------------------------------- */

export interface TemplateStatusDisplay {
  readonly label: string;
  /** One line saying what the state means for whoever is looking at it. */
  readonly meaning: string;
}

/**
 * One entry per member of the fixed `TemplateStatus` enum.
 *
 * Only two of the four can happen to an email template, and the other two are
 * worded for what they will mean rather than left as placeholders. `pending` and
 * `rejected` belong to WhatsApp, where Meta reviews a template before it may be
 * used; for email the tenant approves their own, because there is no external
 * reviewer to wait for. The API refuses to set either of them here, so a badge
 * showing one would mean a WhatsApp template had reached an email screen.
 *
 * Words only. The badge colour for each status is `TEMPLATE_STATUS_TONE` in
 * `components/TemplateStatusBadge.tsx`, the one mapping both editors read since
 * slice 66 put "With Meta" on `warn`. This table had its own `tone` field until
 * then, saying `info` for the same status; it was deleted rather than left
 * unread, because a second tone table that still compiles is the one the next
 * screen picks up, and then two screens colour one status two ways.
 */
export const TEMPLATE_STATUS_DISPLAY: Readonly<Record<TemplateStatus, TemplateStatusDisplay>> = {
  draft: {
    label: 'Draft',
    meaning: 'Still being built. A campaign cannot pick a draft.',
  },
  approved: {
    label: 'Approved',
    meaning: 'Finished and available to a campaign.',
  },
  pending: {
    label: 'With Meta',
    meaning: 'Submitted to Meta for review. WhatsApp only — email is never in this state.',
  },
  rejected: {
    label: 'Rejected by Meta',
    meaning: 'Meta refused this template. WhatsApp only — email is never in this state.',
  },
};

/* -------------------------------------------------------------------------- */
/* Approval blockers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The heading above each blocker's own message.
 *
 * **Headings, not repairs.** The repair is the sentence `templateApprovalIssues`
 * produced, which the API sends and the screen prints unchanged; restating it
 * here would give a person two accounts of the same problem that can disagree.
 * What this adds is which of the five it is, at a glance, above the paragraph
 * that says what to do about it.
 */
export const TEMPLATE_APPROVAL_BLOCKER_TITLE: Readonly<Record<TemplateApprovalBlocker, string>> = {
  missing_subject: 'No subject line',
  empty_body: 'Nothing in the template but the footer',
  missing_sender_address: 'No business address in the footer',
  unresolvable_variable: 'A variable a contact cannot supply',
  attachment_hurts_deliverability: 'This template carries an attachment',
};

/* -------------------------------------------------------------------------- */
/* Variables                                                                   */
/* -------------------------------------------------------------------------- */

export interface VariableInsert {
  /** `{{ name }}` — what is inserted, and what is shown on the control. */
  readonly token: string;
  /** What it resolves to when a campaign sends. */
  readonly meaning: string;
}

/**
 * The variables the editor offers, **derived from the contract**.
 *
 * `CONTACT_VARIABLE_NAMES` is `contactSchema.keyof().extract([...])` in
 * `@wend/shared`, so this list is the contact contract's own keys. A hand-written
 * list here would keep offering `{{ phone }}` for a week after the field was
 * renamed, and every template built in that week would fail to save.
 */
export const VARIABLE_INSERTS: readonly VariableInsert[] = CONTACT_VARIABLE_NAMES.map((name) => ({
  token: templateVariableToken(name),
  meaning:
    name === 'name'
      ? 'The contact’s name'
      : name === 'email'
        ? 'The address the message is going to'
        : 'The contact’s phone number, in E.164',
}));

/**
 * How to reach a column that was uploaded rather than one Wend knows about.
 *
 * Worth saying explicitly, because writing `{{ Company }}` and expecting an
 * uploaded column is the mistake people actually make — and it is refused at
 * save with a message naming the token, which is the right place to catch it but
 * a poor place to learn the syntax.
 */
export const EXTRAS_VARIABLE_HELP = `Any column from an uploaded list works too — write ${templateVariableToken(
  `${EXTRAS_VARIABLE_PREFIX}Company`,
)} using the column’s own heading. It resolves per contact when the campaign sends, and a contact without that column gets nothing in its place.`;

/* -------------------------------------------------------------------------- */
/* Routing a validation failure to the block that caused it                    */
/* -------------------------------------------------------------------------- */

export interface TemplateIssues {
  /** Block id → the messages about that block. */
  readonly byBlockId: ReadonlyMap<string, readonly string[]>;
  /** `name`, `subject`, `preheader`, `replyTo`, `bodyWidth`, `background` → their messages. */
  readonly byField: Readonly<Record<string, readonly string[]>>;
  /** Everything that could not be attributed to one block or one field. */
  readonly general: readonly string[];
}

const EMPTY_ISSUES: TemplateIssues = { byBlockId: new Map(), byField: {}, general: [] };

/** The fields of the settings form a message can be about. */
const SETTINGS_FIELDS = new Set(['name', 'subject', 'preheader', 'replyTo', 'bodyWidth', 'background']);

/**
 * Works out which block, if any, a validation failure is about.
 *
 * The API names the offender in three different ways, because three different
 * layers refuse a bad document and each knows the document differently:
 *
 *   - `blocks.3.text` — the request schema. zod paths carry the **array index**,
 *     which is all a parser can know;
 *   - `blocks.<blockId>.text` — the write path's variable check, which has the
 *     document and can name the **block id**. This is the one that survives a
 *     reorder, which is why block ids exist;
 *   - `blocks` — a failure about the document as a whole: the footer is missing,
 *     an image is not uploaded to this template.
 *
 * All three are useful and none of them is a format this screen gets to choose,
 * so they are all resolved here — an index against the document currently on
 * screen, an id by lookup — and anything that resolves to neither is shown at
 * the top rather than dropped. **A message this function cannot place is still a
 * message a person needs**; silently discarding it would leave a save that fails
 * for no visible reason, which is worse than a message in the wrong place.
 */
export function routeTemplateIssues(
  fieldErrors: readonly FieldError[],
  blocks: readonly TemplateBlock[],
): TemplateIssues {
  if (fieldErrors.length === 0) return EMPTY_ISSUES;

  const byBlockId = new Map<string, string[]>();
  const byField: Record<string, string[]> = {};
  const general: string[] = [];

  const ids = new Set(blocks.map((block) => block.id));

  const addToBlock = (blockId: string, message: string): void => {
    const existing = byBlockId.get(blockId);
    if (existing === undefined) byBlockId.set(blockId, [message]);
    else existing.push(message);
  };

  for (const { path, message } of fieldErrors) {
    const segments = path.split('.');
    const head = segments[0];

    if (head !== 'blocks') {
      if (head !== undefined && SETTINGS_FIELDS.has(head)) {
        (byField[head] ??= []).push(message);
      } else {
        general.push(message);
      }
      continue;
    }

    const target = segments[1];
    if (target === undefined) {
      general.push(message);
      continue;
    }

    // A numeric segment is an index into the document as it is on screen. It is
    // only meaningful while that document is the one that was sent — which it is
    // here, because the editor sends what it is holding and this runs on the
    // answer.
    if (/^\d+$/.test(target)) {
      const block = blocks[Number(target)];
      if (block === undefined) general.push(message);
      else addToBlock(block.id, message);
      continue;
    }

    if (ids.has(target)) addToBlock(target, message);
    else general.push(message);
  }

  return { byBlockId, byField, general };
}

/**
 * Two sets of messages, shown together.
 *
 * The editor holds more than one at a time on purpose: what the last save was
 * refused for, and what the live preview is refusing right now. They are about
 * the same document and belong on the same block, so they are merged for display
 * rather than fighting over one slot — with duplicates dropped, because the same
 * sentence twice reads as two problems.
 */
export function mergeIssues(first: TemplateIssues, second: TemplateIssues): TemplateIssues {
  if (!hasIssues(first)) return second;
  if (!hasIssues(second)) return first;

  const byBlockId = new Map<string, readonly string[]>(first.byBlockId);
  for (const [blockId, messages] of second.byBlockId) {
    byBlockId.set(blockId, unique([...(byBlockId.get(blockId) ?? []), ...messages]));
  }

  const byField: Record<string, readonly string[]> = { ...first.byField };
  for (const [field, messages] of Object.entries(second.byField)) {
    byField[field] = unique([...(byField[field] ?? []), ...messages]);
  }

  return { byBlockId, byField, general: unique([...first.general, ...second.general]) };
}

function unique(messages: readonly string[]): string[] {
  return [...new Set(messages)];
}

/** The messages about one block, or none. */
export function blockIssues(issues: TemplateIssues, blockId: string): readonly string[] {
  return issues.byBlockId.get(blockId) ?? [];
}

/** The messages about one settings field, or none. */
export function fieldIssues(issues: TemplateIssues, field: string): readonly string[] {
  return issues.byField[field] ?? [];
}

/** True when nothing at all came back — used to decide whether to show anything. */
export function hasIssues(issues: TemplateIssues): boolean {
  return issues.byBlockId.size > 0 || Object.keys(issues.byField).length > 0 || issues.general.length > 0;
}

export const NO_TEMPLATE_ISSUES = EMPTY_ISSUES;

/* -------------------------------------------------------------------------- */
/* The preview frame                                                           */
/* -------------------------------------------------------------------------- */

/**
 * What the screen says about the frame the preview renders in.
 *
 * The frame is sandboxed with no tokens at all — no scripts, no same-origin, no
 * forms, no navigation — which is stated on screen because it is the answer to
 * the obvious question about rendering HTML in an app, and because it explains
 * the one visible consequence: an inline image cannot be fetched from inside it.
 */
export const PREVIEW_SANDBOX_NOTE =
  'The preview renders in a sandboxed frame with scripts, forms, navigation and same-origin access all switched off.';

/**
 * Why inline images show as their alt text in the preview.
 *
 * An inline image is served by the API behind the same guard as everything else,
 * and the sandboxed frame has no origin and carries no session — so its request
 * for the bytes is refused and the client falls back to the alt text. Said only
 * when the document actually has an image block, because otherwise it is a
 * paragraph about something that is not on the screen.
 */
export const PREVIEW_IMAGES_NOTE =
  'Inline images show as their alt text here. The frame is sandboxed and carries no session, so the API refuses its request for the image bytes — which is also what a recipient whose mail client blocks images will see.';
